import { PerformanceMetric, usePerformance } from '@/context/performance';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useRef, useState } from 'react';
import { AppBundle } from '../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import { plumaContextValueStore, plumaThemeStore } from '@/components/connected-pluma-provider';
import { useMountEffect } from '@/hooks/use-mount-effect';

// Types for dynamically loaded Pipelines app
type PipelinesRoot = {
  render: (element: React.ReactElement) => void;
  unmount: () => void;
};

type PipelinesReactDOMClient = {
  createRoot: (container: Element, options?: { identifierPrefix?: string }) => PipelinesRoot;
};

type PipelinesAppComponent = React.ComponentType<{
  basename?: string;
  useNewWorkspacesUrls?: boolean;
  plumaContextValue: typeof plumaContextValueStore;
  plumaTheme: typeof plumaThemeStore;
}>;

type PipelinesReact = {
  createElement: (type: unknown, props: Record<string, unknown> | null, ...children: unknown[]) => unknown;
};

type PipelinesModule = {
  rootComponent: PipelinesAppComponent;
  ReactDOMClient: PipelinesReactDOMClient;
  // React 18 instance from the embedded app — used to create elements so that
  // the React 18 reconciler receives a compatible $$typeof symbol
  // (Symbol.for('react.element') vs React 19's 'react.transitional.element').
  React: PipelinesReact;
};

export default function Pipelines(props?: { basename?: string }) {
  return <PipelinesBackgroundApp basename={props?.basename} />;
}

export function PipelinesNonBackgroundApp(props?: { basename?: string }) {
  const ref = useRef(null);
  const rendered = useRef(false);
  const root = useRef<PipelinesRoot | null>(null);
  const [loaded, setIsLoaded] = useState(false);
  const recordPerformanceMetric = usePerformance();

  useMountEffect(() => {
    setChildAppLoaded(false);
  });

  useMountEffect(() => {
    const load = async () => {
      recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
      const {
        rootComponent: PipelinesApp,
        ReactDOMClient: PipelinesReactDomClient,
        React: PipelinesReact,
      } = (await window.importShim(AppBundle.Pipelines)) as PipelinesModule;
      setIsLoaded(true);

      if (ref.current == null) {
        return;
      }

      root.current ??= PipelinesReactDomClient.createRoot(ref.current, { identifierPrefix: 'cdp_' });

      if (!rendered.current && root.current != null) {
        root.current.render(
          PipelinesReact.createElement(PipelinesApp, {
            basename: props?.basename,
            useNewWorkspacesUrls: props?.basename == null,
            plumaContextValue: plumaContextValueStore,
            plumaTheme: plumaThemeStore,
          }) as React.ReactElement,
        );
        rendered.current = true;
      }
    };

    void load();

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        rendered.current = false;
        root.current = null;
        setIsLoaded(false);
      }
    };
  });

  return (
    <>
      <div ref={ref} style={{ display: loaded ? 'flex' : 'none', minWidth: 0, flex: 1 }}></div>
      {loaded ? null : <PageLoader />}
    </>
  );
}

export function PipelinesBackgroundApp(props?: { basename?: string }) {
  const rendered = useRef(false);
  const root = useRef<PipelinesRoot | null>(null);
  const renderPipelinesApp = async (appWrapper: HTMLDivElement) => {
    const {
      rootComponent: PipelinesApp,
      ReactDOMClient: PipelinesReactDomClient,
      React: PipelinesReact,
    } = (await window.importShim(AppBundle.Pipelines)) as PipelinesModule;

    root.current ??= PipelinesReactDomClient.createRoot(appWrapper, { identifierPrefix: 'cdp_' });

    if (!rendered.current && root.current != null) {
      root.current.render(
        PipelinesReact.createElement(PipelinesApp, {
          basename: props?.basename,
          useNewWorkspacesUrls: props?.basename == null,
          plumaContextValue: plumaContextValueStore,
          plumaTheme: plumaThemeStore,
        }) as React.ReactElement,
      );
      rendered.current = true;
    }

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        root.current = null;
        rendered.current = false;
      }
    };
  };

  return <AppBundleLoader appBundle={AppBundle.Pipelines} className="pipelines-app" renderApp={renderPipelinesApp} />;
}
